import { Controller } from "@hotwired/stimulus"
import {
  openChrome,
  closeChrome,
  closeChromeThenNavigate
} from "../helpers/overlay_chrome"

// A follow-up box counts as answered from this many non-space characters.
const MIN_NOTE_LENGTH = 3

// One-time account-created welcome popup. On show it:
//   - hides the native bottom nav (same bridge.hide() the budget carousel uses
//     on its "Add budget" slide) so the FAB does not bleed over the scrim,
//   - locks the dashboard scroll so the background cannot move (the user must
//     pick a CTA),
//   - fires a success haptic (native iOS only, no-op on web).
export default class extends Controller {
  static targets = ["haptic", "chip", "note", "send", "mascot", "follow", "pane", "take", "decline"]
  static values = {
    markUrl: String,
    bankUrl: String,
    feedbackUrl: String,
    openDelay: Number
  }

  connect() {
    // Rendered hidden. A card with no openDelay is waiting to be opened by
    // setup-progress, so it must not lock the page or touch the native chrome
    // yet. One with a delay is the server saying "show this", held back for a
    // beat: on the first launch of a cold app the native nav-bar component is
    // not registered when connect runs, so the dim sent with it is dropped and
    // the bar stays bright over a dimmed screen. Opening on the timer sends the
    // dim once the bridge exists, so the card and the dim land together.
    if (this.element.hidden) {
      if (this.openDelayValue > 0) {
        this._openTimer = setTimeout(() => this._open(), this.openDelayValue)
      }
      return
    }

    this._open()
  }

  // Everything a card does as it comes up. Split out of connect() so the same
  // sequence runs whether the card was on screen from the first paint or was
  // held back by openDelay.
  _open() {
    this.element.hidden = false

    this._lockScroll()
    this._fireHaptic()
    // The budget carousel's activeIndexValueChanged calls bridge.show() for the
    // first slide during load, which would clobber an immediate hide(). Hide
    // now AND after that settles (double rAF) so the popup's hide wins. The
    // popup locks swiping, so the carousel never re-shows it while it is up.
    this._applyChrome()
    requestAnimationFrame(() => requestAnimationFrame(() => this._applyChrome()))
  }

  // Hide the bottom nav + dim the top nav while the popup is up. Runs now AND
  // after a double rAF so it wins over the budget carousel re-showing the
  // bottom nav for the first slide during load.
  _applyChrome() {
    openChrome(this, this.element)
  }

  disconnect() {
    this._unlockScroll()
    if (this._openTimer) clearTimeout(this._openTimer)
  }

  // Welcome popup's two in-page CTAs (voice-modal#open / #openManualForm): mark
  // seen, undim the top nav, then close. The bottom nav is deliberately left
  // hidden — the voice sheet opening in the same tap manages it, and showing it
  // in between would flash the bar for a frame underneath.
  //
  // No announcement: the sheet owns the screen the moment this returns, and a
  // rating ask raised behind it is one the user never sees and the server has
  // already counted as shown.
  accept() {
    this._markSeen()
    closeChrome(this, { showBottomNav: false })
    this._hide()
  }

  // Welcome popup's third route to a first expense ("Connect my bank"), next to
  // the smart-capture and manual buttons. Programmatic because an <a> would let
  // the popup's removal race the navigation.
  visitBank() {
    this._markSeen()
    this._hide()

    if (!this.hasBankUrlValue || !this.bankUrlValue) {
      closeChrome(this)
      return
    }

    closeChromeThenNavigate(this, () => window.Turbo.visit(this.bankUrlValue))
  }

  // ── cancel-feedback popup ──
  // One reason at a time: each chip answers back with its own mascot and its
  // own pane (the 50% offer, or a box that must be filled before Done arms).
  pickReason(event) {
    const reason = event.currentTarget.dataset.reason
    this.chipTargets.forEach((chip) => chip.classList.toggle("selected", chip.dataset.reason === reason))
    this.mascotTargets.forEach((img) => img.classList.toggle("is-on", img.dataset.reason === reason))

    const pane = this.paneTargets.find((p) => p.dataset.reason === reason)
    this.paneTargets.forEach((p) => { p.hidden = p !== pane })
    if (this.hasFollowTarget) this.followTarget.classList.toggle("open", Boolean(pane))

    const offering = Boolean(pane) && this.hasTakeTarget && reason === "too_expensive"
    this.sendTarget.hidden = offering
    if (this.hasTakeTarget) this.takeTarget.hidden = !offering
    if (this.hasDeclineTarget) this.declineTarget.hidden = !offering

    this._armSend()
  }

  noteChanged() {
    this._armSend()
  }

  _armSend() {
    const note = this._activeNote()
    this.sendTarget.disabled = !this._selectedReason() ||
      (note !== null && note.value.trim().length < MIN_NOTE_LENGTH)
  }

  _selectedReason() {
    return this.chipTargets.find((chip) => chip.classList.contains("selected"))?.dataset.reason || null
  }

  _activeNote() {
    return this.noteTargets.find((note) => !note.closest("[hidden]")) || null
  }

  // Sends the reason (and the note, when its pane asked for one) then closes.
  // The only way out of this popup: it carries no dismiss button and its scrim
  // is inert, so the teardown restores the dashboard chrome the way the other
  // stay-put CTAs do. The offer link runs this too, and its own href still
  // opens Apple's redeem sheet because the click is not prevented.
  sendFeedback() {
    const reason = this._selectedReason()
    if (!reason) return

    const body = new FormData()
    body.append("reasons[]", reason)
    const note = this._activeNote()
    if (note) body.append("note", note.value)
    this._post(this.feedbackUrlValue, body)

    closeChrome(this)
    this._hide()
    this._announceClosed()
  }

  // The card is vertically centred, so the iOS keyboard would cover it the
  // moment the note is focused. Pin it to the top while the keyboard is up.
  liftForKeyboard() {
    this.element.style.alignItems = "flex-start"
  }

  dropAfterKeyboard() {
    this.element.style.alignItems = ""
  }

  // Posts the popup's mark-url. For the welcome popup it only records a
  // first-engaged timestamp: that one keeps returning until the account
  // actually has an expense (see BudgetsController#flag_welcome_celebration),
  // because all three of its CTAs merely open something the user can abandon.
  _markSeen() {
    if (!this.hasMarkUrlValue || !this.markUrlValue) return
    this._post(this.markUrlValue)
  }

  _post(url, body = null) {
    if (!url) return
    const token = document.querySelector('meta[name="csrf-token"]')?.content
    fetch(url, {
      method: "POST",
      headers: { "X-CSRF-Token": token || "", "Accept": "text/plain" },
      body,
      keepalive: true
    })
  }

  // Hidden rather than removed: the welcome card is rung 1 of the activation
  // arc and the dashboard's "Continue the setup" button reopens it.
  _hide() {
    this.element.hidden = true
  }

  // Tells anything waiting for a clear dashboard — the rating ask, which must
  // never land on top of a celebration — that the screen is theirs.
  //
  // Deliberately NOT part of _hide(). Every CTA hides the card, but the ones
  // that navigate or open a sheet hide it on the way somewhere else, and the
  // rating ask raised behind those is a card the user sees for a single frame
  // while the server counts it as shown — spending the one ask a day an
  // account gets (User#review_prompt_due?). Only a close that leaves the user
  // looking at the dashboard announces.
  _announceClosed() {
    document.dispatchEvent(new CustomEvent("celebration:closed"))
  }

  _fireHaptic() {
    if (!this.hasHapticTarget) return
    const haptic = this.application.getControllerForElementAndIdentifier(
      this.hapticTarget, "bridge--haptic"
    )
    haptic?.tap()
  }

  _lockScroll() {
    this._prevOverflow = document.documentElement.style.overflow
    document.documentElement.style.overflow = "hidden"
  }

  _unlockScroll() {
    document.documentElement.style.overflow = this._prevOverflow || ""
  }
}
